// @flow

import window from 'window';

import _ from 'lodash';

import TraceKit from 'tracekit';

import $ from 'jquery';
import Cookies from 'js-cookie';

import { runtimeId } from 'runtime-id';
import { isLikeApiRelatedError } from 'error';

import NotificationTraceKitContextSubscriber from './js/notifications';
import SaveSplunkLogMessageForClientSubscriber from './js/splunk';
import StackTraceAnalyzer from './js/utils/stack-trace-analyzer';
import { traceKitToSblModelMapper } from './js/utils/tracekit-to-sbl-model-mapper';
import { REST_API_VERSION } from 'server-api-versioning';

/*
 * This a TraceKit extension for reporting errors to BL
 *
 * Augmented methods:
 *   `TraceKit.report` - Make it accept message string
 * Additional methods:
 *   `TraceKit.context` - Register a context which can add additional data to reports
 *   `TraceKit.tryReport` - Like `report` but does not throw, also accepts string as argument
 *   `TraceKit.notifications` - Boolean options to enable unhandled error notifications
 *   `TraceKit.exposeOuterScriptErrors` - boolean option to enable exposing errors of outer scripts
 *   `TraceKit.utils.*`
 */

// Do not attach sources to reports (otherwise you get endless minified jQuery sources in them)
TraceKit.remoteFetching = false;
// Wrap `setInterval` and `setTimeout`
TraceKit.extendToAsynchronousCallbacks();
// Do not notify user about errors by default
TraceKit.notifications = false;
// Suppress outer script errors
TraceKit.exposeOuterScriptErrors = false;

// Do not submit more then this number of reports
const SUBMITTED_REPORTS_LIMIT = 3;

const stackTraceAnalyzer = new StackTraceAnalyzer();

function submit (report) {
	const data = traceKitToSblModelMapper(report);

	return $.ajax({
		method: 'POST',
		url: `/api/rest/v${REST_API_VERSION}/logs/runtime`,
		dataType: 'json',
		contentType: 'application/json; charset=utf-8',
		cache: false,
		data: JSON.stringify(data, null, 1),
	});
}

const contexts = {};

const contextSubscriptions = [];

TraceKit.context = _.extend(function (id, context, options) {
	options = _.extend({
		// Override contexts with same 'id'
		override: false,

		// Remove context after it was dumped once
		once: false,
	}, options);

	if ('function' === typeof context) {
		context = { dump: context };
	}

	context = (context: Object);

	context.once = Boolean(options.once);

	if (contexts[id]) { // context with this id already registered
		if (options.override) {
			contexts[id].dispose();
		} else {
			id = id + '#' + Math.random().toString().slice(2);
		}
	}

	contexts[id] = context;

	function dispose () {
		delete contexts[id];
	}

	context.dispose = dispose;

	return context;
}, {
	subscribe: function (fn) {
		contextSubscriptions.push(fn);
	},
	unsubscribe: function (fn) {
		contextSubscriptions.splice($.inArray(fn, contextSubscriptions), 1);
	},
});

const TraceKit_report = TraceKit.report;
TraceKit.report = _.extend(function (e) {
	let error;
	if ('string' === typeof e) {
		error = new Error(e);
	} else {
		error = e;
	}
	return TraceKit_report.call(this, error);
}, TraceKit_report);

let lastException = null;

const TraceKit_computeStackTrace = TraceKit.computeStackTrace;
TraceKit.computeStackTrace = _.extend(function (e) {
	lastException = e;
	return TraceKit_computeStackTrace.call(this, e);
}, TraceKit_computeStackTrace);

TraceKit.tryReport = function (e, options) {
	let error;
	if (e instanceof Error) {
		error = e;
	} else {
		error = new Error(String(e));
	}
	try {
		console.error(e);
	} catch (e1) {
		// noop
	}
	lastException = e;
	const trace = TraceKit.computeStackTrace(error);
	onReport(trace, options);
};

let submittedReports = 0;
let lastErrorStringified = null;

function onReport (error, options) {
	// NOTE: Cross-origin script error won't provide errorObj,
	// so tracekit won't trigger TraceKit.computeStackTrace. We gotta manually set last exception,
	// to exclude breeding of errors while reading props from null (e.g. context filler 'error.name')
	lastException = lastException || new Error(error.message);

	//#region HACK
	// WORKAROUND: With 'onerror' handler enabled, we can get two identical
	// errors twice (both with { mode: 'stack' }, is this a TraceKit BUG?)
	const errorStringified = JSON.stringify(error);
	if (lastErrorStringified === errorStringified) {
		return;
	}
	lastErrorStringified = errorStringified;
	setTimeout(function () {
		lastErrorStringified = null;
	}, 1);
	//#endregion

	const isOuterScriptError = _.isEmpty(error.stack)
		? !isLikeApiRelatedError(lastException)
		: stackTraceAnalyzer.isOuterScriptError(error.stack);

	options = _.extend({
		// Pass true if this report must not be shown to user
		quiet: isOuterScriptError && !TraceKit.exposeOuterScriptErrors,

		isOuterScriptError: isOuterScriptError,
	}, options);

	const report = {};

	_.each(contexts, function (ctx, id) {
		let rep;
		try {
			rep = ctx.dump(error, report, lastException);
			JSON.stringify(rep); // check that it's serializable
		} catch (e) {
			rep = {
				'<error while dumping context>': TraceKit.computeStackTrace(e),
			};
		}
		if (ctx.once) {
			ctx.dispose();
		}

		report[id] = rep;

		if (rep && rep._special_ && typeof rep._special_ === 'object') {
			_.each(rep._special_, function (v, k) {
				report._special_[k] = v || report._special_[k];
			});
		}
	});

	report._special_.quiet = options.quiet;
	report._special_.isOuterScriptError = options.isOuterScriptError;

	_.each(contextSubscriptions, function (fn) {
		try {
			fn(report, lastException);
		} catch (e) {
			// noop
		}
	});

	if (submittedReports < SUBMITTED_REPORTS_LIMIT) {
		submittedReports += 1;
		submit(report);
	}
}

TraceKit.report.subscribe(onReport);

TraceKit.context('_special_', function () {
	return {
		productType: 'dictionary',
		id: 'unset',
	};
});

(function () {
	let reportsCounter = 0;

	function ts () {
		return new Date().getTime();
	}
	function defaultId (name) {
		return [name, ts()].join('-');
	}

	TraceKit.context('TraceKit.Generic.Report', function (report) {
		const reportID = defaultId('report');
		return _.extend(report, {
			_special_: {
				'id': runtimeId,
			},
			'id': reportID,
			'runtimeId': runtimeId,
			'index': reportsCounter++,
		});
	});
}());

TraceKit.context('TraceKit.Generic.Page', function () {
	return {
		'location.href': window.location.href,
		'document.title': window.document.title,
	};
});

const startup = new Date();
TraceKit.context('TraceKit.Generic.Date', function () {
	const now = new Date();
	return {
		'start': startup.toString(),
		'now': now.toString(),
		'passed': now.getTime() - startup.getTime(),
	};
});

TraceKit.context('TraceKit.Generic.Browser', function () {
	return {
		'navigator.userAgent': window.navigator.userAgent,
	};
});

TraceKit.context('Rgs.Site.Session', function () {
	return {
		'PHPSESSID': Cookies.get('PHPSESSID'),
	};
});

TraceKit.context('SblModelData', function () {
	return {
		'QuotationCaseID': null,
		'OrderID': null,
		'UserProfileID': null,
	};
});

TraceKit.context('meta.build.version', function () {
	return '__VERSION__'; // replaced with actual build version by browserify
});

TraceKit.context('calc.common.caseID', function (report, options, error) {
	return _.get(error, 'context.calc.common.caseID');
});

TraceKit.context('error.name', function (report, options, error) {
	return error.name;
});

TraceKit.context('error.message', function (report, options, error) {
	return error.message;
});

TraceKit.context('error.userFriendlyMessages', function (report, options, error) {
	return error.userFriendlyMessages;
});

TraceKit.context('error.response.status', function (report, options, error) {
	return _.get(error, 'response.status');
});

TraceKit.context('error.response.headers', function (report, options, error) {
	// FRAGILE: Depends on github fetch polyfill implementation details
	return _.get(error, 'response.headers.map');
});

TraceKit.context('error.response.responseJSON', function (report, options, error) {
	return _.get(error, 'response.responseJSON');
});

// TODO
// TraceKit.context('Rgs.Site.RgsAuthInfo', function () {
// 	return {
// 		'Utils.RgsAuthInfo': Utils.RgsAuthInfo
// 	};
// });

TraceKit.utils = {
	xhr: (function () {
		const optsKey = '__TraceKit_extensions_xhr_options_hack_' + Math.random();

		$(window.document).ajaxSend(function (event, xhr, opts) {
			xhr[optsKey] = opts;
		});

		return function (xhr) {
			return function () {
				return {
					options: xhr[optsKey],
					status: xhr.status,
					statusText: xhr.statusText,
					responseText: xhr.responseText,
				};
			};
		};
	}()),
};

(new NotificationTraceKitContextSubscriber()).subscribe(TraceKit);
(new SaveSplunkLogMessageForClientSubscriber()).subscribe(TraceKit);

module.exports = TraceKit;
